import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, Mail } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import { IMG, SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

export const Hero = () => {
  const { t, lang } = useLang();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 80, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden pt-32 lg:pt-40" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-[var(--lime)]/25 blur-[140px]" />
      <div className="pointer-events-none absolute -right-20 bottom-0 h-[460px] w-[460px] rounded-full bg-[var(--gold)]/25 blur-[140px]" />

      <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-8 lg:px-10">
        <motion.div style={{ y: yText, opacity }} className="relative z-10 flex flex-col justify-center lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="inline-flex w-fit items-center gap-3 rounded-full border border-[var(--line)] bg-white/70 px-4 py-2 backdrop-blur"
            data-testid="hero-overline"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--green)]" />
            <span className="font-mono-label text-[10.5px] text-[var(--ink-2)]">{h.overline}</span>
          </motion.div>

          <MaskedLines
            key={lang}
            lines={h.lines}
            className="font-display mt-8 text-5xl font-bold leading-[0.95] text-[var(--ink)] sm:text-6xl lg:text-[5.5rem]"
            delay={0.25}
          />

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[var(--ink-2)] md:text-lg"
            data-testid="hero-subtext"
          >
            {h.text}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link to="/contact" className="btn-primary" data-testid="hero-cta-quote">
              {h.cta} <ArrowUpRight size={16} />
            </Link>
            <a href={`mailto:${SITE.email}`} className="btn-ghost" data-testid="hero-cta-email">
              <Mail size={16} /> {SITE.email}
            </a>
          </motion.div>

          <motion.dl
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.1, delayChildren: 1.3 } } }}
            className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-[var(--line)] pt-8"
            data-testid="hero-stats"
          >
            {h.stats.map((s) => (
              <motion.div key={s.k} variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }}>
                <dt className="font-display text-3xl font-bold tracking-tight md:text-4xl">{s.k}</dt>
                <dd className="mt-1 font-mono-label text-[10px] text-[var(--muted)]">{s.v}</dd>
              </motion.div>
            ))}
          </motion.dl>
        </motion.div>

        <div className="relative lg:col-span-5" style={{ perspective: 1400 }}>
          <motion.div
            style={{ y: yImg, rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            initial={{ opacity: 0, clipPath: "inset(40% 0 40% 0 round 32px)" }}
            animate={{ opacity: 1, clipPath: "inset(0% 0 0% 0 round 32px)" }}
            transition={{ duration: 1.4, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}
            className="relative aspect-[4/5] overflow-hidden rounded-[32px] shadow-[0_40px_80px_-30px_rgba(14,61,34,0.45)]"
            data-testid="hero-image-frame"
          >
            <img src={IMG.heroPeppers} alt="Colored bell peppers" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <div className="absolute left-5 top-5 rounded-2xl bg-white/95 p-2.5 shadow-lg backdrop-blur" data-testid="hero-logo-badge">
              <img src="/logo.png" alt="Sebastian's Produce logo" className="h-16 w-auto md:h-20" />
            </div>
            <div className="absolute bottom-6 left-6 right-6 text-white" style={{ transform: "translateZ(40px)" }}>
              <p className="font-serif-accent text-3xl italic leading-none">{h.imgTitle}</p>
              <p className="mt-2 font-mono-label text-[10px] text-white/75">{h.imgSub}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30, rotate: -4 }}
            animate={{ opacity: 1, x: 0, rotate: -4 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="absolute -left-6 bottom-16 hidden w-44 overflow-hidden rounded-2xl border-4 border-[var(--canvas)] shadow-xl sm:block lg:-left-16"
          >
            <img src={IMG.basil} alt="Fresh basil and herbs" className="aspect-square w-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: 5 }}
            animate={{ opacity: 1, y: 0, rotate: 5 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="absolute -right-4 -top-6 hidden w-36 overflow-hidden rounded-2xl border-4 border-[var(--canvas)] shadow-xl sm:block lg:-right-10"
          >
            <img src={IMG.vineTomatoes} alt="Tomatoes" className="aspect-square w-full object-cover" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
